// src/utils/themeTokens.js
//
// Derives every extra shade the UI needs from a theme's own colours (themeConfig.js).
// Nothing here introduces a new hue: each value is a theme colour, a mix of two theme
// colours (or of a theme colour and white), or a theme colour with transparency.
// themeConfig.js stays the single source of truth for each palette.

export const FONT_FAMILY =
  "'Plus Jakarta Sans', 'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, Roboto, sans-serif";

// Corner radii, kept in one place so CSS and antd agree.
export const RADIUS = {
  shell: 14, // sidebar, header bar, content panel
  card: 10, // cards, tables, modals
  control: 8, // buttons, inputs, menu items
  small: 6, // tags, chips
};

const parseHex = (hex) => {
  let h = String(hex).trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((ch) => ch + ch).join('');
  const n = parseInt(h.slice(0, 6), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const toHex = ({ r, g, b }) =>
  '#' +
  [r, g, b]
    .map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0'))
    .join('');

// weight = share of `a` in the result (0..1)
export const mix = (a, b, weight) => {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return toHex({
    r: ca.r * weight + cb.r * (1 - weight),
    g: ca.g * weight + cb.g * (1 - weight),
    b: ca.b * weight + cb.b * (1 - weight),
  });
};

export const alpha = (hex, a) => {
  const { r, g, b } = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const luminance = (hex) => {
  const { r, g, b } = parseHex(hex);
  const lin = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
};

const contrast = (a, b) => {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

export const deriveTokens = (c) => {
  const surface = mix(c.cardBackground, '#ffffff', 0.5); // cards, tables, modals
  const surfaceRaised = mix(c.cardBackground, '#ffffff', 0.25); // cards nested in a surface

  return {
    surface,
    surfaceRaised,
    cardTranslucent: alpha(c.cardBackground, 0.88), // sticky top bar over scrolling content
    line: alpha(c.text, 0.1),
    lineStrong: alpha(c.text, 0.17),
    wash: alpha(c.text, 0.055),
    washStrong: alpha(c.text, 0.09),
    muted: mix(c.text, c.cardBackground, 0.68),
    subtle: mix(c.text, c.cardBackground, 0.45),
    link: mix(c.primary, c.text, 0.82),
    // Text colour that reads best directly on the page background
    onPage: contrast(c.text, c.pageBackground) >= contrast('#ffffff', c.pageBackground) ? c.text : '#ffffff',
    focusRing: alpha(c.primary, 0.18),
    hoverWash: alpha(c.hover, 0.12),
    rowHover: alpha(c.hover, 0.07),

    // Each semantic theme colour as a soft background plus a readable text colour
    primarySoft: mix(c.primary, surface, 0.1),
    primaryInk: mix(c.primary, c.text, 0.8),
    positiveSoft: mix(c.hover, surface, 0.16),
    positiveInk: mix(c.hover, c.text, 0.62),
    warningSoft: mix(c.warning, surface, 0.2),
    warningWash: alpha(c.warning, 0.09),
    warningInk: mix(c.warning, c.text, 0.55),
    dangerSoft: mix(c.danger, surface, 0.14),
    dangerInk: mix(c.danger, c.text, 0.68),
    activeSoft: mix(c.activeBackground, surface, 0.3),

    shadowSm: `0 1px 2px ${alpha(c.text, 0.06)}`,
    shadow: `0 1px 2px ${alpha(c.text, 0.06)}, 0 10px 24px -14px ${alpha(c.text, 0.28)}`,
    shadowShell: `0 1px 2px ${alpha(c.text, 0.05)}, 0 20px 40px -26px ${alpha(c.text, 0.45)}`,
    shadowPop: `0 12px 32px -8px ${alpha(c.text, 0.25)}, 0 2px 6px ${alpha(c.text, 0.08)}`,
  };
};

// Every CSS custom property the app uses: the theme's own 19 colours plus the derived set.
export const buildCssVariables = (c) => {
  const t = deriveTokens(c);
  return {
    '--theme-primary': c.primary,
    '--theme-secondary': c.secondary,
    '--theme-page-background': c.pageBackground,
    '--theme-card-background': c.cardBackground,
    '--theme-active-background': c.activeBackground,
    '--theme-danger': c.danger,
    '--theme-hover': c.hover,
    '--theme-danger-hover': c.dangerHover || c.hover,
    '--theme-warning': c.warning,
    '--theme-sidebar-background': c.sidebarBackground,
    '--theme-active-text': c.activeText,
    '--theme-hover-text': c.hoverText || '#ffffff',
    '--theme-text': c.text,
    '--theme-border': c.border,
    '--theme-logo-background': c.logoBackground || c.cardBackground,
    '--theme-help-background': c.helpBackground || c.cardBackground,
    '--theme-profile-background': c.profileBackground || c.cardBackground,
    '--theme-disabled-background': c.disabledBackground || '#D1D1D1',
    '--theme-disabled-text': c.disabledText || '#ffffff',

    '--theme-surface': t.surface,
    '--theme-surface-raised': t.surfaceRaised,
    '--theme-card-translucent': t.cardTranslucent,
    '--theme-line': t.line,
    '--theme-line-strong': t.lineStrong,
    '--theme-wash': t.wash,
    '--theme-wash-strong': t.washStrong,
    '--theme-muted': t.muted,
    '--theme-subtle': t.subtle,
    '--theme-link': t.link,
    '--theme-on-page': t.onPage,
    '--theme-focus-ring': t.focusRing,
    '--theme-hover-wash': t.hoverWash,
    '--theme-row-hover': t.rowHover,
    '--theme-primary-soft': t.primarySoft,
    '--theme-primary-ink': t.primaryInk,
    '--theme-positive-soft': t.positiveSoft,
    '--theme-positive-ink': t.positiveInk,
    '--theme-warning-soft': t.warningSoft,
    '--theme-warning-wash': t.warningWash,
    '--theme-warning-ink': t.warningInk,
    '--theme-danger-soft': t.dangerSoft,
    '--theme-danger-ink': t.dangerInk,
    '--theme-active-soft': t.activeSoft,
    '--theme-shadow-sm': t.shadowSm,
    '--theme-shadow': t.shadow,
    '--theme-shadow-shell': t.shadowShell,
    '--theme-shadow-pop': t.shadowPop,
  };
};

// antd ConfigProvider theme. antd computes hover/active/background shades in JS, so it
// must receive real colour values, never var(--...) strings. Shades antd would derive
// badly from very dark primaries (e.g. Forest Green) are set explicitly.
export const buildAntdTheme = (c) => {
  const t = deriveTokens(c);
  const primaryBorder = mix(c.primary, t.surface, 0.35);

  return {
    token: {
      fontFamily: FONT_FAMILY,
      fontSize: 14,
      fontSizeHeading1: 30,
      fontSizeHeading2: 24,
      fontSizeHeading3: 20,
      fontSizeHeading4: 17,
      fontSizeHeading5: 15,
      fontWeightStrong: 600,
      borderRadius: RADIUS.control,
      borderRadiusLG: RADIUS.card,
      borderRadiusSM: RADIUS.small,
      borderRadiusXS: 4,
      controlHeight: 36,
      controlHeightSM: 28,
      controlHeightLG: 42,
      wireframe: false,
      motionDurationMid: '0.18s',

      colorPrimary: c.primary,
      colorPrimaryHover: c.hover,
      colorPrimaryActive: c.secondary,
      colorPrimaryBg: t.primarySoft,
      colorPrimaryBgHover: mix(c.primary, t.surface, 0.2),
      colorPrimaryBorder: primaryBorder,
      colorPrimaryBorderHover: mix(c.primary, t.surface, 0.5),
      colorPrimaryText: t.primaryInk,
      colorPrimaryTextHover: c.hover,
      colorPrimaryTextActive: c.secondary,
      colorInfo: c.primary,
      colorInfoBg: t.primarySoft,
      colorInfoBorder: primaryBorder,
      colorLink: t.link,
      colorLinkHover: c.hover,
      colorLinkActive: c.secondary,
      colorError: c.danger,
      colorErrorHover: c.dangerHover || c.danger,
      colorErrorText: t.dangerInk,
      colorWarning: c.warning,
      colorWarningText: t.warningInk,

      colorText: c.text,
      colorTextHeading: c.text,
      colorTextLabel: t.muted,
      colorTextSecondary: t.muted,
      colorTextDescription: t.muted,
      colorTextTertiary: t.subtle,
      colorTextPlaceholder: alpha(c.text, 0.42),
      colorIcon: t.muted,
      colorIconHover: c.text,

      colorBgLayout: c.pageBackground,
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgMask: alpha(c.text, 0.35),
      colorBorder: t.lineStrong,
      colorBorderSecondary: t.line,
      colorSplit: t.line,
      colorFillAlter: t.wash,
      colorFillContent: t.wash,
      colorFillSecondary: t.wash,
      colorFillTertiary: t.wash,
      colorFillQuaternary: alpha(c.text, 0.03),
      controlItemBgHover: t.wash,
      controlItemBgActive: t.primarySoft,
      controlItemBgActiveHover: mix(c.primary, t.surface, 0.2),
      controlOutline: t.focusRing,
      controlOutlineWidth: 3,

      boxShadow: t.shadowPop,
      boxShadowSecondary: t.shadowPop,
      boxShadowTertiary: t.shadowSm,
    },
    components: {
      Layout: {
        bodyBg: c.pageBackground,
        headerBg: 'transparent',
        siderBg: c.sidebarBackground,
        footerBg: 'transparent',
      },
      Menu: {
        itemBg: 'transparent',
        subMenuItemBg: 'transparent',
        itemColor: c.text,
        itemHoverColor: c.text,
        itemHoverBg: t.hoverWash,
        itemActiveBg: alpha(c.hover, 0.18),
        itemSelectedBg: c.activeBackground,
        itemSelectedColor: c.activeText,
        itemBorderRadius: RADIUS.control,
        itemHeight: 40,
        itemMarginInline: 0,
        itemMarginBlock: 3,
        iconSize: 17,
        collapsedIconSize: 18,
        activeBarBorderWidth: 0,
      },
      Card: {
        colorBgContainer: t.surface,
        colorBorderSecondary: t.line,
        headerFontSize: 15,
        headerFontSizeSM: 14,
        headerHeight: 54,
        headerHeightSM: 44,
      },
      Table: {
        colorBgContainer: t.surface,
        headerBg: t.surface,
        headerColor: t.muted,
        headerSplitColor: 'transparent',
        headerSortActiveBg: t.wash,
        headerSortHoverBg: t.wash,
        bodySortBg: 'transparent',
        rowHoverBg: t.rowHover,
        rowSelectedBg: t.primarySoft,
        rowSelectedHoverBg: mix(c.primary, t.surface, 0.2),
        borderColor: t.line,
        footerBg: t.surface,
        fixedHeaderSortActiveBg: t.wash,
        cellPaddingBlock: 12,
        cellPaddingBlockMD: 10,
        cellPaddingBlockSM: 8,
      },
      Modal: {
        contentBg: t.surface,
        headerBg: t.surface,
        footerBg: t.surface,
        titleFontSize: 17,
      },
      Tabs: {
        itemColor: t.muted,
        itemHoverColor: c.text,
        itemSelectedColor: c.text,
        itemActiveColor: c.text,
        inkBarColor: c.primary,
        cardBg: t.wash,
        titleFontSize: 14,
        horizontalItemGutter: 28,
        verticalItemPadding: '9px 14px',
        verticalItemMargin: '2px 0 0 0',
      },
      Button: {
        fontWeight: 600,
        primaryShadow: 'none',
        defaultShadow: 'none',
        dangerShadow: 'none',
        defaultBg: '#ffffff',
        defaultColor: c.text,
        defaultBorderColor: t.lineStrong,
        defaultHoverBg: '#ffffff',
        defaultHoverColor: c.text,
        defaultHoverBorderColor: mix(c.primary, '#ffffff', 0.45),
        defaultActiveBg: '#ffffff',
        defaultActiveColor: c.text,
        defaultActiveBorderColor: c.primary,
        textHoverBg: t.wash,
        colorBgTextActive: t.washStrong,
        contentFontSizeLG: 15,
      },
      Tag: {
        defaultBg: t.wash,
        defaultColor: c.text,
      },
      Segmented: {
        trackBg: t.wash,
        itemSelectedBg: '#ffffff',
        itemColor: t.muted,
        itemHoverColor: c.text,
        itemSelectedColor: c.text,
      },
      Select: {
        optionSelectedBg: t.primarySoft,
        optionActiveBg: t.wash,
        optionSelectedColor: c.text,
        multipleItemBg: t.wash,
      },
      DatePicker: {
        cellActiveWithRangeBg: t.primarySoft,
        cellHoverBg: t.wash,
      },
      Descriptions: {
        labelBg: t.wash,
      },
      Alert: {
        withDescriptionPadding: '14px 18px',
      },
      Statistic: {
        titleFontSize: 13,
        contentFontSize: 26,
      },
      Pagination: {
        itemActiveBg: '#ffffff',
      },
      Divider: {
        colorSplit: t.line,
      },
      Tooltip: {
        colorBgSpotlight: c.text,
      },
    },
  };
};
